import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { describeSegments } from "@/lib/runSegments";
import { toast } from "sonner";
import { ChevronLeft, Play, Clock, Gauge, MapPin, Target, Flame, Snowflake, Repeat, Lightbulb, ArrowUpCircle, Wrench, Map as MapIcon } from "lucide-react";

const Section = ({ icon: Icon, title, children, testid }) => (
  <Card className="rounded-2xl bg-card border-border p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-[#ccff0c]" />
      <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{title}</p>
    </div>
    {children}
  </Card>
);

export default function RunWorkoutDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get(`/run-workouts/${id}`).then(({ data }) => setW(data)).catch(() => { toast.error("Workout not found"); navigate("/run-workouts"); });
  }, [id, navigate]);

  if (!w) {
    return (
      <AppShell title="Workout">
        <div className="space-y-3"><Skeleton className="h-28 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>
      </AppShell>
    );
  }

  const outline = describeSegments(w.segments);

  return (
    <AppShell
      title={undefined}
      hideNav
      right={<Button size="sm" data-testid="back-to-list" onClick={() => navigate("/run-workouts")} className="bg-secondary border border-border text-foreground"><ChevronLeft className="h-4 w-4" /> Back</Button>}
    >
      <div data-testid="run-workout-detail" className="space-y-3 pb-24">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20">{w.run_type}</Badge>
            <Badge className="bg-secondary border border-border text-muted-foreground">{w.difficulty}</Badge>
          </div>
          <h1 className="font-display text-3xl font-bold leading-tight">{w.workout_name}</h1>
          <p className="text-muted-foreground mt-1.5">{w.workout_summary}</p>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-2">
          {[[Clock, "Duration", w.estimated_duration], [MapPin, "Distance", w.estimated_distance], [Gauge, "Target RPE", w.target_rpe], [Target, "Goal", w.primary_goal]].map(([Icon, l, v]) => (
            <Card key={l} className="rounded-xl bg-card border-border p-3">
              <div className="flex items-center gap-1.5 mb-0.5"><Icon className="h-3.5 w-3.5 text-[#ccff0c]" /><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</p></div>
              <p className="text-sm font-semibold leading-snug">{v}</p>
            </Card>
          ))}
        </div>

        {/* Structured outline */}
        <Section icon={Repeat} title="Workout structure" testid="workout-structure">
          <div className="space-y-1.5">
            {outline.map((line, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {line.repeat ? (
                  <>
                    <span className="font-mono font-bold text-[#ccff0c] flex-shrink-0">{line.rounds}×</span>
                    <span className="text-muted-foreground">{line.text}</span>
                  </>
                ) : (
                  <span className="text-foreground">{line.text}</span>
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Flame} title="Warm-up"><p className="text-sm text-muted-foreground">{w.warm_up}</p></Section>
        <Section icon={Play} title="Main workout"><p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{w.main_workout}</p></Section>
        <Section icon={Snowflake} title="Cool-down"><p className="text-sm text-muted-foreground">{w.cool_down}</p></Section>

        <Section icon={Lightbulb} title="Coach notes">
          <ul className="space-y-1.5">
            {(w.coach_notes || []).map((c, i) => (
              <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-[#ccff0c]">•</span>{c}</li>
            ))}
          </ul>
        </Section>

        <div className="grid grid-cols-1 gap-3">
          <Section icon={ArrowUpCircle} title="Make it easier"><p className="text-sm text-muted-foreground">{w.beginner_modification}</p></Section>
          <Section icon={ArrowUpCircle} title="Make it harder"><p className="text-sm text-muted-foreground">{w.progression_option}</p></Section>
        </div>

        <Section icon={Wrench} title="Equipment & where">
          <div className="flex flex-wrap gap-1.5">
            {(w.equipment || []).map((e, i) => <Badge key={`e${i}`} className="bg-secondary border border-border text-muted-foreground text-[11px] font-normal">{e}</Badge>)}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(w.location_options || []).map((l, i) => <Badge key={`l${i}`} className="bg-[#ccff0c]/10 border border-[#ccff0c]/20 text-[#ccff0c] text-[11px] font-normal"><MapIcon className="h-3 w-3 mr-1" />{l}</Badge>)}
          </div>
        </Section>
      </div>

      {/* Sticky start bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/90 backdrop-blur" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="mx-auto max-w-[560px] px-4 py-3">
          <Button data-testid="start-guided-run" onClick={() => navigate(`/run-workouts/${w.id}/guided`)} className="w-full h-13 py-3.5 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-bold text-base rounded-2xl">
            <Play className="h-5 w-5 mr-1.5" /> Start Workout
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
